// src/oura/screens/ClientPage.tsx
//
// The merchant's screens, rendered inside the merchant shell (OuraSidebar,
// or the bottom bar on a phone). Four views, one per nav item beside Visibility: Home
// (`progress`), Work, Decisions and Reports. Each view lives in its own file
// under src/oura/merchant; this one only picks which, and mounts the drawers
// every view can open.
//
// The reader is a business owner on a phone between customers. So: one
// sentence, one number, one button, then space. No codes, no reference
// numbers, no process words. Anything a merchant does not need in order to
// act is a tap away, never on the page by default.
//
// Copy rules from the catalogue apply: no jargon unless the merchant said it
// first, name the fix not the flaw, never a number from a memory run. The
// score is deliberately absent until the platform's number is one we would
// stand behind in front of a client.

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { ClientPagePayload } from "@service/aeo/clientReview";
import { Home } from "../merchant/Home";
import { merchantPayload, type MerchantLive } from "../merchant/model";
import { Work } from "../merchant/Work";
import { Decisions } from "../merchant/Decisions";
import { Reports } from "../merchant/Reports";
import { MerchantDrawersProvider } from "../merchant/MerchantDrawers";

/** `progress` is Home, served at /oura/dashboard. */
export type ClientView = "progress" | "work" | "decisions" | "reports";

export function ClientPage({ payload: served, view, focusItemId, onRefresh, live }: {
    payload: ClientPagePayload;
    view: ClientView;
    focusItemId?: string | null;
    onRefresh: () => Promise<void>;
    /** Home's named count and searches, from the business detail. Undefined while it loads. */
    live: MerchantLive | null | undefined;
}) {
    // An in-page link (/oura/work#plan, and /oura/schedule#plan redirected
    // there) lands on its card. The router leaves the scroll where it was, and
    // the card only exists once the view has rendered, so the scroll happens
    // here, once for each hash and view; a refresh of the page does not move it.
    // The one place the merchant's payload enters, and so the one place a line
    // the merchant is not shown is dropped (#3946). Every screen below reads
    // `payload`, so none of them can be the one that forgets.
    const payload = useMemo(() => merchantPayload(served), [served]);

    const { hash } = useLocation();
    useEffect(() => {
        if (hash) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: "start" });
    }, [hash, view]);

    return (
        <MerchantDrawersProvider payload={payload} onRefresh={onRefresh}>
            <div className="mx-auto max-w-[1500px] pb-16">
                {view === "decisions" && <Decisions payload={payload} focusItemId={focusItemId} />}
                {view === "progress" && <Home payload={payload} live={live} />}
                {view === "reports" && <Reports payload={payload} />}
                {/* Home sums the month up and links here for every piece of
                    work, the 12 months and every request (19 Oct 2026), so
                    /oura/work (the bell's request links, bookmarks, #plan)
                    opens Our work again, which reads its ?request=. */}
                {view === "work" && <Work payload={payload} />}
            </div>
        </MerchantDrawersProvider>
    );
}
